import { setAuthority } from '@/utils/infrastructure/auth/authority';
import jwtDecode from 'jwt-decode';
import {
  queryAllowedEnvironments,
  queryAllowedSites,
  queryChangeCompany,
  queryChangeEnvironment,
  queryCompanies,
} from './service';

const initialState = {
  data: [],
  sites: [],
  latestFetchCompanies: null,
  latestFetchAllowedEnvironments: null,
  userInfoCompany: null,
  searchCriteria: '',
  currentEnvironment: '',
  currentCompany: '',
  searchContext: false,
};

const Model = {
  namespace: 'userContext',
  state: initialState,
  effects: {
    *fetchContextData({ payload }, { call, put }) {
      try {
        const res = yield call(queryCompanies, payload);
        yield put({ type: 'queryContextData', payload: res });
      } catch (e) {}
    },

    *fetchCompanies({ payload }, { call, put }) {
      try {
        const res = yield call(queryCompanies, payload);
        yield put({ type: 'queryContextData', payload: res });
      } catch (e) {}
    },

    *fetchAllowedEnvironments({ payload }, { call, put }) {
      try {
        const res = yield call(queryAllowedEnvironments, payload);
        yield put({
          type: 'queryContextData',
          payload: _.isArray(res) ? res : { context: 'environment', value: res > 1 },
        });
      } catch (e) {}
    },

    *fetchAllowedSites({ payload }, { call, put }) {
      try {
        const { items, totalCount } = yield call(queryAllowedSites, payload);
        yield put({
          type: 'queryContextData',
          payload: { context: 'site', value: !!totalCount, items },
        });
      } catch (e) {}
    },

    *allowedSites({ payload }, { put, select }) {
      let data = yield select((state) => state.userContext.sites);
      if (payload.search) {
        data = _.filter(
          data,
          (o) =>
            _.toLower(o.name).includes(payload.search) ||
            _.toLower(o.shortCode).includes(payload.search),
        );
      }
      yield put({ type: 'queryContextData', payload: data });
    },

    *changeCompany({ payload }, { call, put }) {
      try {
        const res = yield call(queryChangeCompany, payload);
        yield put({ type: 'fetchAllowedEnvironments', payload: { count: true } });
        yield put({ type: 'fetchAllowedSites' });
        yield put({
          type: 'changeContext',
          payload: {
            ...res,
            ..._.pick(payload, ['context']),
          },
        });

        // Update authority after reducer completes to avoid Redux violations
        try {
          const user = jwtDecode(res.accessToken);
          setAuthority(user.role);
        } catch (e) {}

        yield put({ type: 'chat/resetChatState' });

        payload.onFinish?.();
      } catch (e) {}
    },

    *changeEnvironment({ payload }, { call, put }) {
      try {
        const res = yield call(queryChangeEnvironment, payload);
        yield put({
          type: 'changeContext',
          payload: {
            ...res,
            ..._.pick(payload, ['context']),
          },
        });

        // Update authority after reducer completes to avoid Redux violations
        try {
          const user = jwtDecode(res.accessToken);
          setAuthority(user.role);
        } catch (e) {}

        yield put({ type: 'chat/resetChatState' });

        payload.onFinish?.();
      } catch (e) {}
    },

    *search({ payload }, { put }) {
      yield put({ type: 'querySearch', payload });
    },

    *defaultContext({ payload }, { call, put, select }) {
      try {
        const storedContext = localStorage.getItem('x-userContext');
        let {
          companyCount = 0,
          environmentCount = 0,
          siteCount = 0,
          agentCount,
          ...existingContext
        } = JSON.parse(storedContext || '{}', true);

        const isEmptyContext = !storedContext || (!companyCount && !environmentCount && !siteCount);
        const checked = yield select((st) => st.userContext.changeContext);

        if (!checked && isEmptyContext) {
          companyCount = yield call(queryCompanies, { count: true });
          environmentCount = yield call(queryAllowedEnvironments, { count: true });
          const sites = yield call(queryAllowedSites);

          siteCount = sites.totalCount;
          // Merge with new values, preserving agentCount if it exists
          const contextToSave = {
            ...existingContext,
            companyCount,
            environmentCount,
            siteCount,
            ...(agentCount !== undefined && { agentCount }),
          };

          localStorage.setItem('x-userContext', JSON.stringify(contextToSave));
        }

        yield put({
          type: 'queryDefaultContext',
          payload: {
            ...payload,
            ...(!checked && {
              changeContext: {
                company: companyCount > 1,
                environment: environmentCount > 1,
                site: !!siteCount,
              },
            }),
          },
        });
      } catch (e) {}
    },

    *toggleSearchContext({ payload }, { put }) {
      yield put({ type: 'querySearchContext', payload });
    },

    *cleanup({ payload }, { put }) {
      yield put({ type: 'reset' });
    },
  },
  reducers: {
    queryContextData(state, { payload }) {
      return {
        ...state,
        ...(!payload.context && { data: payload }),
        ...(payload.context && {
          ...(payload.context === 'site' && { data: payload.items }),
          changeContext: {
            ...state.changeContext,
            [payload.context]: payload.value,
          },
        }),
      };
    },

    changeContext(state, { payload }) {
      const context = {};
      try {
        const currentUser = {};
        const user = jwtDecode(payload.accessToken);
        const authUser = JSON.parse(localStorage.getItem('x-auth-user'));
        const {
          companyId,
          companyCommercialName,
          companyName,
          environmentId,
          environmentName,
          positionId,
          positionName,
        } = user;

        switch (state.searchContext || payload.context) {
          case 'company': {
            _.set(context, 'currentCompany', { id: companyId, name: companyName });
            _.set(authUser, 'position', { id: positionId, name: positionName });
            _.set(authUser, 'company', {
              id: companyId,
              commercialName: companyCommercialName,
              name: companyName,
            });
            break;
          }

          case 'environment':
            _.set(context, 'currentEnvironment', { id: environmentId, name: environmentName });
            _.set(authUser, 'defaultEnvironment', { id: environmentId, name: environmentName });
            break;

          default:
            break;
        }

        localStorage.setItem('x-auth-user', JSON.stringify(currentUser));
        localStorage.setItem('x-auth-token', JSON.stringify(payload));
      } catch (e) {}

      return {
        ...state,
        ...context,
        searchCriteria: '',
      };
    },

    setSearchCriteria(state, { payload }) {
      return {
        ...state,
        searchCriteria: payload,
      };
    },

    querySearchContext(state, { payload }) {
      return {
        ...state,
        searchContext: payload,
        searchCriteria: '',
      };
    },

    queryDefaultContext(state, { payload }) {
      const { changeContext, sites, ...user } = payload || {};
      return {
        ...state,
        ...(changeContext && { changeContext }),
        currentCompany: {
          id: user.companyId ?? user.company?.id,
          name: user.companyName ?? user.company?.name,
        },
        currentEnvironment: {
          id: user.environmentId ?? user.defaultEnvironment?.id,
          name: user.environmentName ?? user.defaultEnvironment?.name,
        },
        sites,
      };
    },

    querySearch(state, { payload }) {
      return {
        ...state,
        searchCriteria: payload,
      };
    },

    reset() {
      return initialState;
    },
  },
};

export default Model;
